import currency from "currency.js";
import { Engine } from "json-rules-engine";
import { getOptionListByOverride, getPhotoPricingForSize } from "./optionOverrides";

export function getVariantTotal(variant, basePrice) {
  let total = 0;
  if (variant) {
    for (const [, value] of Object.entries(variant)) {
      if (value?.pricing) {
        if (value.pricing.amount) {
          total += +value.pricing.amount;
        } else if (value.pricing.factor) {
          total += currency(basePrice).multiply(value.pricing.factor).value;
        }
      } else if (typeof value === "object") {
        total += getVariantTotal(value, basePrice);
      }
    }
  }
  return +total;
}

function findOptionByName(options, name) {
  return (options || []).find((o) => o.name === name);
}

function applyPhotoPricingFromSize(tmp, variant) {
  if (!variant?.size) return;
  for (const [key, value] of Object.entries(tmp)) {
    if (key === "size") continue;
    if (value.field.type === "photo") {
      const pricing = getPhotoPricingForSize(value, variant.size.name);
      if (pricing) {
        value.pricing = pricing;
      } else {
        delete value.pricing;
      }
    }
  }
}

function applyLegacySelectOverrides(tmp, variant) {
  if (!variant?.size) return;
  for (const [key, value] of Object.entries(tmp)) {
    if (key === "size") continue;
    if (value.field.type === "select") {
      tmp[key].field.options = getOptionListByOverride(value.field.options, {
        size: variant.size.name,
      });
      if (tmp[key].field.options.length === 0) {
        delete tmp[key];
      }
    }
  }
}

/**
 * Merges `params` from every `override` event. Later events win for the same
 * `fieldId` / `optionName` keys (shallow merge per option row).
 */
export function mergeOverrideParamsFromEvents(overrideEvents) {
  const merged = {};
  for (const ev of overrideEvents) {
    const p = ev?.params && typeof ev.params === "object" ? ev.params : {};
    for (const [fieldId, fieldMap] of Object.entries(p)) {
      if (fieldMap == null || typeof fieldMap !== "object") continue;
      if (!merged[fieldId]) merged[fieldId] = {};
      for (const [optName, row] of Object.entries(fieldMap)) {
        const prev = merged[fieldId][optName];
        const next =
          row != null && typeof row === "object"
            ? { ...(prev && typeof prev === "object" ? prev : {}), ...row }
            : row;
        merged[fieldId][optName] = next;
      }
    }
  }
  return merged;
}

function applyRuleOverrideToSelectFields(tmp, variant, ruleParams) {
  for (const [key, value] of Object.entries(tmp)) {
    if (key === "size") continue;
    if (value.field.type !== "select" || !variant.size) continue;
    const rawOptions = JSON.parse(JSON.stringify(value.field.options || []));
    const nextOptions = [];
    for (const option of rawOptions) {
      const { override, ...optClone } = option;
      const row = ruleParams[key]?.[optClone.name];

      if (row?.allow === false || String(row?.allow) === "false") {
        continue;
      }

      if (option.pricing && +option?.pricing?.amount) {
        optClone.pricing = { type: "fixed", amount: +option?.pricing?.amount };
      }

      if (row != null && +row.amount) {
        optClone.pricing = { type: "fixed", amount: +row.amount };
      }

      nextOptions.push(optClone);
    }
    tmp[key].field.options = nextOptions;
    if (tmp[key].field.options.length === 0) {
      delete tmp[key];
    }
  }
}

/**
 * Re-applies canonical option objects from `fields` onto existing selections so stored pricing
 * matches rule-driven option rows (e.g. fixed amounts from overrides).
 */
export function applyVariantPricingFromFields(variant, fields, basePrice) {
  if (!variant || !fields) return { variant, changed: false };
  let next = { ...variant };
  let changed = false;

  for (const [key, fieldConfig] of Object.entries(fields)) {
    if (fieldConfig.field?.type !== "select") continue;
    const v = next[key];
    if (!v) continue;
    const options = fieldConfig.field.options || [];
    const isMulti = +fieldConfig.field?.max > 1;
    const parts = v.value?.parts;

    if (parts != null) {
      const filteredParts = {};
      for (const k of Object.keys(parts).sort((a, b) => +a - +b)) {
        const optFromField = findOptionByName(options, parts[k]?.name);
        if (optFromField) {
          const { override, ...opt } = optFromField;
          filteredParts[k] = opt;
        }
      }
      if (!Object.keys(filteredParts).some((k) => filteredParts[k]?.name)) continue;
      const newEntry = {
        ...v,
        name: v.name,
        rank: v.rank || fieldConfig.rank || 1,
        pricing: { amount: getVariantTotal(filteredParts, basePrice) },
        value: { ...v.value, parts: filteredParts },
      };
      const before = next[key];
      if (
        JSON.stringify(before?.value?.parts) !== JSON.stringify(newEntry.value?.parts) ||
        before?.value?.selection !== newEntry.value?.selection ||
        before?.pricing?.amount !== newEntry.pricing?.amount
      ) {
        next = { ...next, [key]: newEntry };
        changed = true;
      }
    } else if (isMulti && Array.isArray(v.value)) {
      const picked = v.value.map((sel) => findOptionByName(options, sel.name)).filter(Boolean);
      if (picked.length === 0) continue;
      const newEntry = {
        name: picked.map((o) => o.name).join(", "),
        rank: v.rank || fieldConfig.rank || 1,
        pricing: { amount: getVariantTotal(picked, basePrice) },
        value: picked.map((o) => {
          const { override, ...opt } = o;
          return opt;
        }),
      };
      if (JSON.stringify(next[key]) !== JSON.stringify(newEntry)) {
        next = { ...next, [key]: newEntry };
        changed = true;
      }
    } else {
      const name = v.value?.name ?? v.name;
      if (!name) continue;
      const optFromField = findOptionByName(options, name);
      if (!optFromField) continue;
      const { override, ...opt } = optFromField;
      const newEntry = { ...opt, value: opt };
      if (v.rank != null) {
        newEntry.rank = v.rank;
      }
      if (JSON.stringify(next[key]) !== JSON.stringify(newEntry)) {
        next = { ...next, [key]: newEntry };
        changed = true;
      }
    }
  }
  return { variant: next, changed };
}

/**
 * Facts for one catalog rules run: size plus every variant dimension that has a string `name`.
 */
export function buildCatalogRuleFacts(variant) {
  const facts = {};
  const v = variant || {};
  if (v.size?.name) {
    facts.size = v.size.name;
  }
  for (const [k, val] of Object.entries(v)) {
    if (k === "size") continue;
    if (!val) continue;
    if (typeof val.name === "string" && val.name.trim() !== "") {
      facts[k] = val.name;
    }
  }
  return facts;
}

/**
 * Deep-clones `variants`, applies photo pricing by size, then runs `item.rules` through
 * json-rules-engine. All `override` events' `params` are merged, then applied as
 * `params[fieldId][optionName]` allow/amount;
 * otherwise falls back to per-option size overrides via getOptionListByOverride.
 *
 * For select options, each option list is deep-cloned before transforms. When a merged
 * override row has no numeric `amount`, catalog option pricing is preserved and normalized
 * to `{ type: "fixed", amount }` when the catalog definition has a numeric `pricing.amount`.
 * When the rule row supplies a numeric `amount`, it replaces option pricing.
 */
export async function getQuoteVariantsWithRulesAsync({ variants, variant, rules }) {
  const tmp = JSON.parse(JSON.stringify(variants));
  applyPhotoPricingFromSize(tmp, variant);

  if (!Array.isArray(rules) || rules.length === 0) {
    return tmp;
  }

  if (!variant?.size) {
    return tmp;
  }

  const engine = new Engine([], { allowUndefinedFacts: true });
  for (const r of rules) {
    engine.addRule(r);
  }

  const facts = buildCatalogRuleFacts(variant);
  const { events } = await engine.run(facts);
  const overrideEvents = events.filter((e) => e.type === "override");

  if (overrideEvents.length === 0) {
    applyLegacySelectOverrides(tmp, variant);
    return tmp;
  }

  const ruleParams = mergeOverrideParamsFromEvents(overrideEvents);

  applyRuleOverrideToSelectFields(tmp, variant, ruleParams);
  return tmp;
}
